import { useState, useEffect, useCallback } from "react";
import { SchoolLayout } from "./SchoolLayout";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Input } from "@/project/karuna/components/ui/input"; // @ts-ignore - input is not a valid module
import { Label } from "@/project/karuna/components/ui/label"; // @ts-ignore - label is not a valid module
import { Plus, Upload, Search, Edit, Trash2 } from "lucide-react";
import { supabase } from "@/project/karuna/integrations/supabase/client"; // @ts-ignore - supabase is not a valid module
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/project/karuna/components/ui/dialog"; // @ts-ignore - dialog is not a valid module
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/project/karuna/components/ui/table"; // @ts-ignore - table is not a valid module

interface Student {
  id: string;
  name: string;
  roll_no: string;
  email: string;
  mobile: string;
}

const SchoolStudents = () => {
  const { toast } = useToast();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    roll_no: "",
    email: "",
    mobile: "",
  });

  const fetchStudents = useCallback(async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return;
      }

      const { data: schoolData } = await supabase
        .from("schools")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (!schoolData) {
        return;
      }

      const { data, error } = await supabase
        .from("students")
        .select("*")
        .eq("school_id", schoolData.id)
        .order("name");

      if (error) {
        throw error;
      }
      setStudents(
        data?.map(student => ({
          id: student.id,
          name: student.name ?? "",
          roll_no: student.roll_no ?? "",
          email: student.email ?? "",
          mobile: student.mobile ?? "",
        })) ?? []
      );
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        return;
      }

      const { data: schoolData } = await supabase
        .from("schools")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (!schoolData) {
        return;
      }

      const { error } = await supabase.from("students").insert({
        ...formData,
        school_id: schoolData.id,
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: "Student added successfully",
      });

      setDialogOpen(false);
      setFormData({ name: "", roll_no: "", email: "", mobile: "" });
      fetchStudents();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const filteredStudents = students.filter(
    student =>
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.roll_no?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <SchoolLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Student Management</h1>
            <p className="mt-1 text-muted-foreground">
              Manage your school&apos;s student roster
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline">
              <Upload className="mr-2 h-4 w-4" />
              Bulk Upload
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-hero">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Student
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Student</DialogTitle>
                  <DialogDescription>
                    Enter student details to add them to your roster
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={e =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="roll_no">Roll Number</Label>
                    <Input
                      id="roll_no"
                      value={formData.roll_no}
                      onChange={e =>
                        setFormData({ ...formData, roll_no: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={e =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile</Label>
                    <Input
                      id="mobile"
                      value={formData.mobile}
                      onChange={e =>
                        setFormData({ ...formData, mobile: e.target.value })
                      }
                    />
                  </div>
                  <Button type="submit" className="w-full">
                    Add Student
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search students by name or roll number..."
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center">Loading students...</div>
            ) : filteredStudents.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                No students found. Add your first student to get started.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Roll Number</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStudents.map(student => (
                    <TableRow key={student.id}>
                      <TableCell className="font-medium">
                        {student.name}
                      </TableCell>
                      <TableCell>{student.roll_no ?? "-"}</TableCell>
                      <TableCell>{student.email ?? "-"}</TableCell>
                      <TableCell>{student.mobile ?? "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </SchoolLayout>
  );
};

export default SchoolStudents;
